/* eslint-disable react-hooks/exhaustive-deps */
import { Breadcrumb, IBreadcrumbItem, MessageBar, MessageBarType, ProgressIndicator, Stack, TooltipHost } from '@fluentui/react';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AccreditorCourseSettings from '../components/accreditorCourseSettings';
import SupplementalFolder from '../components/assignmentDescriptions';
import CourseWork from '../components/coursework';
import { LeftNav } from '../components/layout/leftnav';
import { getCourse, getStudentsInCourse } from '../data/courses';
import { shouldGetCourseDataFromCanvasAPIs } from '../lib/helper';
import { AssignmentsProvider, useAssignmentContext } from '../providers/assignmentsprovider';
import { useAuthenticationContext } from '../providers/authenticationprovider';
import { useCoursesContext } from '../providers/coursesprovider';
import { useSemesterContext } from '../providers/semesterprovider';
import { ICourse, ISemester, Semester } from '../types/ICourse';
import Syllabus from './syllabus';

export const assignmentSupplementalFolders = ["Assignment Descriptions", "Syllabus", "Supplemental Documents", "Featured Works"];

const Course: React.FunctionComponent<{ courseProp?: ICourse, syllabus?: boolean, supplementalFolder?: boolean, accreditorCourseSettings?: boolean }> = ({ courseProp, syllabus, supplementalFolder, accreditorCourseSettings }) => {
    const navigate = useNavigate();
    const params = useParams();
    const coursesContext = useCoursesContext();

    let defaultCourse: ICourse | undefined = undefined;
    if (courseProp) {
        defaultCourse = courseProp;
    }
    if (!defaultCourse && coursesContext.Result) {
        defaultCourse = coursesContext.Result.find(c => c.SIS === params?.id);
    }

    const authContext = useAuthenticationContext();
    const semesterContext = useSemesterContext();
    const assignmentContext = useAssignmentContext();
    const [errorText, setErrorText] = useState<any>();
    const [course, setCourse] = useState<ICourse | undefined>(defaultCourse);
    const [students, setStudents] = useState<any[]>();
    const [loadingCourse, setLoadingCourse] = useState<boolean>(false);
    const [shouldCourseGetCourseDataFromCanvasAPIs, setShouldCourseGetCourseDataFromCanvasAPIs] = useState<boolean>(false);

    useEffect(() => {
        if ((params?.id && params?.year && params?.semester && authContext.Result) && (!defaultCourse || (defaultCourse !== course))) {
            setLoadingCourse(true);            
            const semester: ISemester = {
                Semester: params.semester as Semester,
                Year: Number(params.year)
            }
            //try get course and set it into state
            getCourse(params.id, params.id, semester, authContext.Result).then(course => {
                if (!course) {
                    setErrorText(`There was no course found with SIS ${params.id} in semester ${semester.Semester} ${semester.Year}`)
                }
                setCourse(course);
                setLoadingCourse(false);
                semesterContext.SetSelectedSemester(semester)
            }).catch(err => {
                setErrorText("Error getting courses")
            })
            authContext.Result && shouldGetCourseDataFromCanvasAPIs(semester, authContext.Result).then(res => {
                setShouldCourseGetCourseDataFromCanvasAPIs(res || false);
            })
        }
    }, [params.id, params.year, params.semester])

    useEffect(() => {
        if(course?.CanvasId && semesterContext.SelectedSemester && authContext.Result && shouldCourseGetCourseDataFromCanvasAPIs)
        {
            getStudentsInCourse(course.CanvasId).then(students => {
                setStudents(students);
            });
        }
    }, [course, semesterContext.SelectedSemester, authContext.Result]);

    const _breadCrumbItems = (): IBreadcrumbItem[] => {
        const semester: ISemester = {
            Semester: params.semester as Semester,
            Year: Number(params.year)
        }
        const retVal: IBreadcrumbItem[] = [];
        const courseBreadCrumbRoot: IBreadcrumbItem = {
            text: `Kepler Course Index`,
            key: "root",
            onClick: () => {
                assignmentContext.SetSelectedAssignment(undefined);
                navigate("/")
            }
        };
        const semesterBreadCrumb: IBreadcrumbItem = {
            text: `${semester.Year} ${semester.Semester}`,
            key: `${semester.Year} ${semester.Semester}`,
            style: {
                color: "rgb(35, 38, 54)"
            }
        };
        retVal.push(courseBreadCrumbRoot);
        retVal.push(semesterBreadCrumb);

        if (course) {
            const fullCourseName: string = `${course?.Department} | ${course?.Title} | ${course?.Instructor} (${course.SIS})`;
            const maxLength = 100;
            const courseName = fullCourseName.length > maxLength ? fullCourseName.substring(0, maxLength) + "..." : fullCourseName;
            let courseBreadCrumb: IBreadcrumbItem = {
                key: "Course",
                text: fullCourseName,
                onRender: (item) => {
                    return <TooltipHost content={fullCourseName}><span style={{fontSize: 18}}>{courseName}</span></TooltipHost>
                }
            };
            retVal.push(courseBreadCrumb);
        }

        return retVal;
    };

    const _renderCourseArea = () => {
        if(syllabus)
        {
            return <Syllabus courseProp={course!} />
        }
        else if(supplementalFolder && params.supplementalFolderName)
        {            
            return <SupplementalFolder />;
        }
        else if(accreditorCourseSettings && course)
        {
            return <AccreditorCourseSettings course={course} />
        }
        else {
            return course && <CourseWork students={students} shouldGetDataFromCanvasAPIs={shouldCourseGetCourseDataFromCanvasAPIs} course={course} />
        }
    }

    return <div>
        
        {errorText && <MessageBar messageBarType={MessageBarType.error}>
            {errorText}
        </MessageBar>}
        {loadingCourse && <ProgressIndicator styles={{root: {paddingLeft: 10}}} label="Loading course..." />}
        {!loadingCourse && course &&
            <AssignmentsProvider course={course} assignmentName={params.assignmentName}>
                <Stack horizontal>
                    <LeftNav course={course} />
                    <Stack.Item grow>
                        <Breadcrumb styles={{root: {marginLeft: 10}, itemLink: {color: "#232636"}}} overflowIndex={1} items={_breadCrumbItems()} />
                        {_renderCourseArea()}
                    </Stack.Item>
                </Stack>
            </AssignmentsProvider>
        }
        {coursesContext.Error && <span>{coursesContext.Error}</span>}
    </div>;
};

export default Course;